// src/components/nanny/MakeOfferModal.jsx
import { X } from 'lucide-react';
import { useState } from 'react';
import FormInput from '../forms/FormInput';
import FormSelect from '../forms/FormSelect';
import Button from '../layout/ui/Button';
import LoadingSpinner from '../common/LoadingSpinner';
import api from '../../lib/api';
import { success, error } from '../common/Toast';

const liveInOptions = [
  { value: '', label: 'No preference' },
  { value: '1', label: 'Yes – Live-in required' },
  { value: '0', label: 'No – Live-out only' },
];

export default function MakeOfferModal({ isOpen, onClose, nanny, onSuccess }) {
  const [amount, setAmount] = useState('');
  const [conditions, setConditions] = useState('');
  const [liveIn, setLiveIn] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen || !nanny) return null;

  const handleSubmit = async () => {
    if (!amount || amount < 10000) {
      error('Please enter a valid monthly salary (minimum ₦10,000)');
      return;
    }

    setLoading(true);
    try {
      await api.post('/offers', {
        nanny_id: nanny.id,
        amount: Number(amount),
        conditions: conditions.trim() || 'Standard terms',
        // You can add live_in field in backend later if needed
        // live_in: liveIn,
      });

      success(`Offer of ₦${Number(amount).toLocaleString()} sent to ${nanny.full_name}!`);
      onSuccess?.();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to send offer';
      error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl max-w-lg w-full my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-8 border-b border-gray-100">
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center text-white text-4xl font-bold shadow-lg">
              {nanny.full_name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Make an Offer</h2>
              <p className="text-lg text-gray-600 mt-1">{nanny.full_name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-3 hover:bg-gray-100 rounded-xl transition-all"
          >
            <X size={28} className="text-gray-500" />
          </button>
        </div>

        {/* Form Body */}
        <div className="p-8 space-y-7">
          {/* Salary Input */}
          <FormInput
            label="Monthly Salary Offer (₦)"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="85000"
            required
            className="text-lg"
          />

          {/* Live-in Preference */}
          <FormSelect
            label="Live-in Required?"
            options={liveInOptions}
            value={liveInOptions.find((opt) => opt.value === liveIn)}
            onChange={(opt) => setLiveIn(opt?.value || '')}
            placeholder="Select preference"
          />

          {/* Conditions */}
          <div>
            <label className="block font-medium text-gray-700 mb-3">
              Additional Conditions <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              rows={5}
              value={conditions}
              onChange={(e) => setConditions(e.target.value)}
              placeholder="• Live-in arrangement&#10;• 6 working days per week&#10;• Feeding included&#10;• Sundays off&#10;• Medical allowance"
              className="w-full px-5 py-4 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition resize-none text-gray-700 placeholder-gray-400"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1 py-4 text-lg font-medium"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              isLoading={loading}
              disabled={loading || !amount}
              className="flex-1 bg-primary hover:bg-primary/90 text-white font-bold text-lg py-4 rounded-2xl shadow-lg transition"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" color="white" className="mr-3" />
                  Sending Offer...
                </>
              ) : (
                'Send Offer'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}